import React from 'react';
import { FileText, MessageSquare, File as FileIcon, GripVertical } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type ResourceKind = 'Document' | 'Chat' | 'PDF';

export interface ProjectListItem {
  id: string;
  title: string;
  kind: ResourceKind;
  typeLabel?: string;
  category?: string;
  createdAt: string;
  updatedAt?: string | null;
}

interface ProjectResourcesListProps {
  items: ProjectListItem[];
  onRowClick: (item: ProjectListItem) => void;
  /**
   * Enables the selection column (checkboxes). When false, the list is read-only.
   */
  selectable?: boolean;
  selectedIds?: Set<string> | string[];
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (checked: boolean) => void;
}

function isIdSelected(selectedIds: Set<string> | string[] | undefined, id: string) {
  if (!selectedIds) return false;
  return Array.isArray(selectedIds) ? selectedIds.includes(id) : selectedIds.has(id);
}

function formatDateSafe(dateStr?: string | null) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function kindIcon(kind: ResourceKind) {
  if (kind === 'Chat') return MessageSquare;
  if (kind === 'PDF') return FileIcon;
  return FileText;
}

function createDragPreview(count: number, label: string, event: React.DragEvent) {
  if (typeof document === 'undefined' || !event.dataTransfer) return;

  const existing = document.getElementById('rcm-drag-preview');
  if (existing && existing.parentElement) {
    existing.parentElement.removeChild(existing);
  }

  const container = document.createElement('div');
  container.id = 'rcm-drag-preview';
  container.style.position = 'fixed';
  container.style.top = '-1000px';
  container.style.left = '-1000px';
  container.style.zIndex = '2147483647';
  container.style.pointerEvents = 'none';
  container.style.borderRadius = '6px';
  container.style.background = 'rgba(15, 23, 42, 0.92)';
  container.style.color = 'white';
  container.style.border = '1px solid rgba(148, 163, 184, 0.6)';
  container.style.padding = '6px 10px';
  container.style.fontSize = '11px';
  container.style.fontFamily =
    'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.gap = '2px';

  const countEl = document.createElement('div');
  countEl.textContent = `${count} item${count === 1 ? '' : 's'}`;
  countEl.style.fontWeight = '600';

  const labelEl = document.createElement('div');
  labelEl.id = 'rcm-drag-preview-label';
  labelEl.textContent = label;
  labelEl.style.opacity = '0.85';

  container.appendChild(countEl);
  container.appendChild(labelEl);

  document.body.appendChild(container);

  const rect = container.getBoundingClientRect();
  const offsetX = rect.width / 2;
  const offsetY = rect.height / 2;

  try {
    event.dataTransfer.setDragImage(container, offsetX, offsetY);
  } catch {
    // If setDragImage fails, let the browser fall back to the default.
  }
}

const kindColors: Record<ResourceKind, string> = {
  Document: 'bg-muted text-muted-foreground',
  Chat: 'bg-primary/10 text-primary',
  PDF: 'bg-destructive/10 text-destructive',
};

export function ProjectResourcesList({
  items,
  onRowClick,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: ProjectResourcesListProps) {
  const allSelected =
    selectable &&
    items.length > 0 &&
    selectedIds &&
    items.every((i) => isIdSelected(selectedIds, i.id));

  const handleSelectAllChange = (checked: boolean | 'indeterminate') => {
    if (!onToggleSelectAll) return;
    onToggleSelectAll(checked === true);
  };

  const buildDragPayload = (item: ProjectListItem) => {
    const itemIsSelected = isIdSelected(selectedIds, item.id);
    const payloadItems =
      itemIsSelected && selectedIds
        ? items.filter((i) => isIdSelected(selectedIds, i.id))
        : [item];
    return JSON.stringify(payloadItems.map(({ id, kind }) => ({ id, kind })));
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        {/* Mobile: list */}
        <div className="md:hidden divide-y divide-border pl-4 pr-6">
          {items.map((item) => {
            const IconComp = kindIcon(item.kind);
            const handleClick = () => onRowClick(item);
            const dragPayload = buildDragPayload(item);
            return (
              <div
                key={item.id}
                onClick={handleClick}
                className="flex items-center gap-3 py-3 active:bg-accent/50 transition-colors cursor-pointer touch-manipulation"
                draggable
                onDragStart={(e) => {
                  try {
                    e.dataTransfer.setData('application/x-rcm-project-item', dragPayload);
                    e.dataTransfer.setData('text/plain', item.title || '');
                  } catch {
                    // ignore
                  }
                  e.dataTransfer.effectAllowed = 'copyMove';
                  let count = 1;
                  if (selectable && selectedIds && isIdSelected(selectedIds, item.id)) {
                    count = Array.isArray(selectedIds) ? selectedIds.length : selectedIds.size;
                  }
                  createDragPreview(count, 'Drag to a library in the sidebar…', e);
                }}
              >
                {selectable && onToggleSelect && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Checkbox
                      checked={isIdSelected(selectedIds, item.id)}
                      onCheckedChange={() => onToggleSelect(item.id)}
                      aria-label={`Select ${item.title}`}
                    />
                  </div>
                )}
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <IconComp className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{item.title}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
                    {item.kind && (
                      <span className="rounded bg-muted px-1.5 py-0.5">
                        {item.kind}
                      </span>
                    )}
                    {item.typeLabel && item.kind === 'Document' && (
                      <span className="rounded bg-muted px-1.5 py-0.5">
                        {item.typeLabel}
                      </span>
                    )}
                    {item.category && (
                      <span className="rounded bg-primary/10 text-primary px-1.5 py-0.5">
                        {item.category}
                      </span>
                    )}
                    <span>{formatDateSafe(item.updatedAt || item.createdAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50 border-border">
                <TableHead className="w-8 pl-4 pr-0" />
                {selectable && onToggleSelect && (
                  <TableHead className="w-12 pl-4 pr-0">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={handleSelectAllChange}
                      aria-label="Select all items"
                    />
                  </TableHead>
                )}
                <TableHead className="font-semibold pl-4 pr-4">Title</TableHead>
                <TableHead className="font-semibold px-4">Type</TableHead>
                <TableHead className="font-semibold px-4">Tag</TableHead>
                <TableHead className="text-right font-semibold pr-6 pl-4">
                  Updated
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const IconComp = kindIcon(item.kind);
                const handleRowClick = () => onRowClick(item);
                const dragPayload = buildDragPayload(item);
                return (
                  <TableRow
                    key={item.id}
                    onClick={handleRowClick}
                    className="cursor-pointer hover:bg-accent/50 transition-colors group border-border"
                    draggable
                    onDragStart={(e) => {
                      try {
                        e.dataTransfer.setData('application/x-rcm-project-item', dragPayload);
                        e.dataTransfer.setData('text/plain', item.title || '');
                      } catch {
                        // ignore
                      }
                      e.dataTransfer.effectAllowed = 'copyMove';

                      let count = 1;
                      if (selectable && selectedIds) {
                        const isSelected =
                          Array.isArray(selectedIds)
                            ? selectedIds.includes(item.id)
                            : selectedIds.has(item.id);
                        if (isSelected) {
                          count = Array.isArray(selectedIds)
                            ? selectedIds.length
                            : selectedIds.size;
                        }
                      }
                      createDragPreview(
                        count,
                        'Drag to a library in the sidebar…',
                        e
                      );
                    }}
                  >
                    <TableCell className="w-8 pl-4 pr-0 py-3 align-middle">
                      <div className="flex items-center justify-center text-muted-foreground group-hover:text-foreground transition-colors">
                        <GripVertical className="h-4 w-4" aria-hidden="true" />
                      </div>
                    </TableCell>
                    {selectable && onToggleSelect && (
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        className="pl-4 pr-0 py-3"
                      >
                        <Checkbox
                          checked={isIdSelected(selectedIds, item.id)}
                          onCheckedChange={() => onToggleSelect(item.id)}
                          aria-label={`Select ${item.title}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium py-3 pl-4 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <IconComp className="h-4 w-4 text-primary" />
                        </div>
                        <span className="truncate">{item.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${kindColors[item.kind]}`}
                        >
                          {item.kind}
                        </span>
                        {item.kind === 'Document' && item.typeLabel && (
                          <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            {item.typeLabel}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      {item.category ? (
                        <span className="inline-flex items-center rounded-md bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium">
                          {item.category}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground py-3 pr-6 pl-4">
                      {formatDateSafe(item.updatedAt || item.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

